import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
  Cell,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/**
 * The 5 CGIAR Impact Areas with their result filter fields and brand colours.
 */
const IMPACT_AREAS = [
  { key: 'gender',       label: 'Gender Equality',        field: 'genderLevel',       color: '#009688' },
  { key: 'climate',      label: 'Climate Adaptation',     field: 'climateLevel',      color: '#2196F3' },
  { key: 'nutrition',    label: 'Nutrition & Health',      field: 'nutritionLevel',    color: '#FF9800' },
  { key: 'biodiversity', label: 'Environmental Health',    field: 'biodiversityLevel', color: '#4CAF50' },
  { key: 'poverty',      label: 'Poverty Reduction',       field: 'povertyLevel',      color: '#9C27B0' },
];

/**
 * Lighten a hex colour toward white by the given amount (0 = unchanged, 1 = white).
 */
function lightenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * Custom tooltip for the stacked bar chart.
 */
function RegionalImpactAreaTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  return (
    <div style={{
      background: '#fff',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      fontSize: '13px',
      lineHeight: 1.6,
    }}>
      <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.area}</div>
      <div style={{ color: d.color, fontWeight: 500 }}>
        Principal: {d.principal}
      </div>
      <div style={{ color: '#6b7280' }}>
        Significant: {d.significant}
      </div>
      <div style={{ color: '#374151', fontWeight: 600, marginTop: 2, borderTop: '1px solid #e5e7eb', paddingTop: 2 }}>
        Total: {d.total}
      </div>
    </div>
  );
}

RegionalImpactAreaTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

/**
 * Custom label renderer that shows the total count at the end of the stacked bar.
 */
function TotalLabel(props) {
  const { x, y, width, height, value, index } = props;
  if (value == null || value === 0) return null;
  // For the significant segment (second stack), x + width gives the right edge of the full bar
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      textAnchor="start"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
    >
      {value}
    </text>
  );
}

TotalLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  value: PropTypes.number,
  index: PropTypes.number,
};

/**
 * RegionalImpactAreaChart
 *
 * Horizontal stacked bar chart showing how a region's results are tagged across
 * the 5 CGIAR Impact Areas, broken down by Principal vs Significant tagging.
 */
const RegionalImpactAreaChart = memo(function RegionalImpactAreaChart({ results, color }) {
  const { chartData, exportData } = useMemo(() => {
    if (!results || results.length === 0) return { chartData: [], exportData: [] };

    const data = IMPACT_AREAS.map(ia => {
      let principal = 0;
      let significant = 0;

      for (const r of results) {
        const level = r[ia.field];
        if (level === 'Principal') principal++;
        else if (level === 'Significant') significant++;
      }

      return {
        area: ia.label,
        principal,
        significant,
        total: principal + significant,
        color: ia.color,
        colorLight: lightenColor(ia.color, 0.4),
      };
    });

    // Sort by total descending
    data.sort((a, b) => b.total - a.total);

    const _exportData = data.map(d => ({
      'Impact Area': d.area,
      'Principal': d.principal,
      'Significant': d.significant,
      'Total': d.total,
    }));

    return { chartData: data, exportData: _exportData };
  }, [results]);

  if (!results || results.length === 0 || chartData.length === 0) {
    return (
      <ChartWrapper title="Impact Area Coverage">
        <NoChartData
          message="No data available"
          detail="No results found for this region to analyse Impact Area coverage."
        />
      </ChartWrapper>
    );
  }

  // Check if every IA has zero tagged results
  const hasAnyData = chartData.some(d => d.total > 0);
  if (!hasAnyData) {
    return (
      <ChartWrapper title="Impact Area Coverage">
        <NoChartData
          message="No Impact Area tagging"
          detail="None of this region's results are tagged to any CGIAR Impact Area."
        />
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper
      title="Impact Area Coverage"
      info="Shows how results in this region are tagged across the 5 CGIAR Impact Areas. Each bar shows the split between Principal (primary focus) and Significant (meaningful contribution) tagging."
      chartData={exportData}
      sourceResults={results}
      exportFilename="regional-impact-area-coverage"
    >
      <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 48)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 50, left: 8, bottom: 4 }}
          barCategoryGap="24%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="area"
            tick={{ fontSize: 11 }}
            width={200}
          />
          <Tooltip
            content={<RegionalImpactAreaTooltip />}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />

          {/* Principal segment — full IA colour */}
          <Bar dataKey="principal" stackId="stack" radius={[0, 0, 0, 0]} name="Principal">
            {chartData.map((entry) => (
              <Cell key={`p-${entry.area}`} fill={entry.color} />
            ))}
          </Bar>

          {/* Significant segment — lighter shade of IA colour */}
          <Bar dataKey="significant" stackId="stack" radius={[0, 4, 4, 0]} name="Significant">
            {chartData.map((entry) => (
              <Cell key={`s-${entry.area}`} fill={entry.colorLight} />
            ))}
            <LabelList
              dataKey="total"
              position="right"
              content={<TotalLabel />}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-3 text-xs text-[var(--text-secondary)]">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ background: '#4CAF50' }}
          />
          <span>Principal (darker)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ background: lightenColor('#4CAF50', 0.4) }}
          />
          <span>Significant (lighter)</span>
        </div>
      </div>
    </ChartWrapper>
  );
});

RegionalImpactAreaChart.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default RegionalImpactAreaChart;
